// src/interfaces/playbookTypes.ts

/**
 * Playbook Tool Type Definitions
 *
 * These types define the schema for playbook tool results that are
 * streamed from the backend when the AI consults the client's
 * contract negotiation playbook.
 */

/**
 * Represents a section header from the playbook markdown
 * Pre-parsed by the backend for navigation UI
 */
export interface PlaybookSection {
  /** Header level: 1 = #, 2 = ##, 3 = ### */
  level: number;
  /** Header text content */
  title: string;
}

/**
 * Successful playbook retrieval content (inner content object)
 */
export interface PlaybookSuccessContent {
  /** Full markdown content of the playbook */
  playbook: string;
  /** Pre-parsed header hierarchy for navigation */
  sections: PlaybookSection[];
  /** Character count of the playbook content */
  content_length: number;
  /** Original document title/filename (e.g., "Anonim Vendor Contracting Playbook Jan 2025.docx") */
  playbook_title?: string;
  /** ISO date string when the playbook was last updated (e.g., "2026-01-15") */
  last_updated?: string;
  /**
   * The playbook that was resolved for this response (auto-selected when only one
   * exists, or fetched by id after the user picked one from a selection card).
   * When present, the chosen id must be persisted for the session so it is sent
   * on subsequent requests.
   */
  selected_playbook?: { id: string; name: string };
}

/**
 * Error content when playbook is not available
 */
export interface PlaybookErrorContent {
  /** Error message (e.g., "No playbook configured") */
  error: string;
}

/**
 * Union type for playbook content (success or error)
 */
export type PlaybookContent = PlaybookSuccessContent | PlaybookErrorContent;

/**
 * Complete playbook tool result payload - NESTED structure from backend
 *
 * Backend sends:
 * {
 *   "type": "tool_result",
 *   "tool_name": "get_client_playbook",
 *   "tool_use_id": "toolu_abc123",
 *   "reason": "Checking policy on liability caps",
 *   "content": {
 *     "playbook": "# Full markdown...",
 *     "sections": [{ "level": 2, "title": "Liability" }, ...],
 *     "content_length": 62683
 *   }
 * }
 */
export interface PlaybookToolResult {
  /** Type identifier */
  type: "tool_result";
  /** Tool name identifier */
  tool_name: "get_client_playbook";
  /** Unique tool use ID for correlation */
  tool_use_id: string;
  /** AI's reason for consulting the playbook */
  reason: string;
  /** The playbook content (nested) */
  content: PlaybookSuccessContent;
}

/**
 * Type guard to check if an object is a PlaybookToolResult
 *
 * Detects the NESTED backend structure:
 * - Has `tool_name` === "get_client_playbook"
 * - Has `reason` (string)
 * - Has `content` object with playbook data
 */
export function isPlaybookToolResult(obj: unknown): obj is PlaybookToolResult {
  if (!obj || typeof obj !== "object") {
    return false;
  }

  const candidate = obj as Record<string, unknown>;

  // Check for nested structure with tool_name identifier
  if (candidate.tool_name === "get_client_playbook") {
    // Nested structure: { tool_name, reason, content: { playbook, sections, content_length } }
    const content = candidate.content as Record<string, unknown> | undefined;
    return (
      typeof candidate.reason === "string" &&
      content !== undefined &&
      typeof content === "object" &&
      typeof content.playbook === "string" &&
      Array.isArray(content.sections) &&
      typeof content.content_length === "number"
    );
  }

  // Fallback: Check for flat structure (backwards compatibility)
  // Some environments may send: { playbook, sections, content_length } directly
  if (
    typeof candidate.playbook === "string" &&
    Array.isArray(candidate.sections) &&
    typeof candidate.content_length === "number"
  ) {
    return true;
  }

  return false;
}

/**
 * Helper to normalize playbook result to consistent format
 * Handles both nested and flat structures
 */
export function normalizePlaybookResult(obj: unknown): NormalizedPlaybookResult | null {
  if (!obj || typeof obj !== "object") {
    return null;
  }

  const candidate = obj as Record<string, unknown>;

  // Nested structure (preferred)
  // Backend sends: { type, tool_name, tool_use_id, reason, content: { playbook, sections, ... } }
  if (candidate.tool_name === "get_client_playbook") {
    const content = candidate.content as PlaybookSuccessContent | undefined;
    if (content && typeof content.playbook === "string") {
      // Extract reason - should be at top level alongside tool_name
      const reason =
        typeof candidate.reason === "string" && candidate.reason.length > 0
          ? candidate.reason
          : "Consulting playbook";

      return {
        playbook: content.playbook,
        sections: content.sections || [],
        content_length: content.content_length || content.playbook.length,
        reason,
        tool_use_id: candidate.tool_use_id as string | undefined,
        // New fields from content object
        playbook_title: content.playbook_title,
        last_updated: content.last_updated,
        selected_playbook: content.selected_playbook,
      };
    }
  }

  // Flat structure (fallback)
  if (
    typeof candidate.playbook === "string" &&
    Array.isArray(candidate.sections) &&
    typeof candidate.content_length === "number"
  ) {
    const reason =
      typeof candidate.reason === "string" && candidate.reason.length > 0
        ? candidate.reason
        : "Consulting playbook";

    return {
      playbook: candidate.playbook as string,
      sections: candidate.sections as PlaybookSection[],
      content_length: candidate.content_length as number,
      reason,
      tool_use_id: candidate.tool_use_id as string | undefined,
      playbook_title: candidate.playbook_title as string | undefined,
      last_updated: candidate.last_updated as string | undefined,
      selected_playbook: candidate.selected_playbook as { id: string; name: string } | undefined,
    };
  }

  return null;
}

/**
 * Normalized playbook data for UI consumption
 * This is what components should use after normalization
 */
export interface NormalizedPlaybookResult {
  playbook: string;
  sections: PlaybookSection[];
  content_length: number;
  reason: string;
  tool_use_id?: string;
  /**
   * Set when the markdown body was deliberately stripped (playbooks run to
   * hundreds of thousands of characters, so they are not written to durable
   * storage). The metadata below is retained and the viewer renders its
   * collapsed card from it, offering a reload instead of the body.
   */
  playbook_omitted?: boolean;
  /** Original document title/filename */
  playbook_title?: string;
  /** ISO date string when playbook was last updated */
  last_updated?: string;
  /**
   * The playbook that was resolved for this response. Persisted to the session
   * so its id is sent on subsequent requests (even when auto-selected).
   */
  selected_playbook?: { id: string; name: string };
}

/**
 * Type guard to check if normalized playbook result has actual content
 */
export function isPlaybookSuccess(result: NormalizedPlaybookResult): boolean {
  return typeof result.playbook === "string" && result.playbook.length > 0;
}

/**
 * Type guard to check if an object is a playbook error response
 * Error case: { error: "No playbook configured" }
 */
export function isPlaybookError(obj: unknown): obj is PlaybookErrorContent {
  if (!obj || typeof obj !== "object") {
    return false;
  }
  const candidate = obj as Record<string, unknown>;
  return "error" in candidate && typeof candidate.error === "string";
}

// =================================================================
// PLAYBOOK SELECTION
// =================================================================
//
// For tenants with multiple playbooks, consulting the playbook tool can return
// an in-chat selection card instead of resolved content. The user picks one,
// the choice is persisted for the session, and the chosen id is sent so the
// backend fetches that playbook.

/**
 * A single playbook option offered in a selection card.
 */
export interface PlaybookSelectionOption {
  id: string;
  name: string;
  description: string;
  status: string;
  last_updated: string;
}

/**
 * Inner content object of a playbook selection tool result.
 *
 * Backend sends:
 * {
 *   "type": "tool_result",
 *   "tool_name": "get_client_playbook",
 *   "tool_use_id": "...",
 *   "reason": "...",
 *   "content": { "type": "playbook_selection", "playbooks": [...] }
 * }
 */
export interface PlaybookSelectionContent {
  type: "playbook_selection";
  playbooks: PlaybookSelectionOption[];
}

/**
 * Normalized playbook selection request for UI consumption.
 */
export interface PlaybookSelectionResult {
  tool_use_id?: string;
  reason: string;
  playbooks: PlaybookSelectionOption[];
}

/**
 * Type guard for a playbook selection tool result.
 *
 * NOTE: This intentionally does NOT overlap with isPlaybookToolResult — the
 * selection card carries `content.type === "playbook_selection"` and a
 * `playbooks` array, whereas resolved content carries `content.playbook`.
 */
export function isPlaybookSelectionResult(
  obj: unknown
): obj is { tool_name: "get_client_playbook"; content: PlaybookSelectionContent } {
  if (!obj || typeof obj !== "object") {
    return false;
  }

  const candidate = obj as Record<string, unknown>;
  if (candidate.tool_name !== "get_client_playbook") {
    return false;
  }

  const content = candidate.content as Record<string, unknown> | undefined;
  return (
    content !== undefined &&
    typeof content === "object" &&
    content.type === "playbook_selection" &&
    Array.isArray(content.playbooks)
  );
}

/**
 * Normalize a playbook selection tool result, coercing each option to a
 * PlaybookSelectionOption with string fallbacks for missing fields.
 */
export function normalizePlaybookSelectionResult(obj: unknown): PlaybookSelectionResult | null {
  if (!isPlaybookSelectionResult(obj)) {
    return null;
  }

  const candidate = obj as Record<string, unknown>;
  const content = candidate.content as PlaybookSelectionContent;

  const reason =
    typeof candidate.reason === "string" && candidate.reason.length > 0
      ? candidate.reason
      : "Choose a playbook";

  const rawPlaybooks: unknown[] = Array.isArray(content.playbooks) ? content.playbooks : [];
  const playbooks: PlaybookSelectionOption[] = rawPlaybooks.map((raw) => {
    const item = (raw || {}) as Record<string, unknown>;
    return {
      id: typeof item.id === "string" ? item.id : String(item.id ?? ""),
      name: typeof item.name === "string" ? item.name : String(item.name ?? ""),
      description: typeof item.description === "string" ? item.description : "",
      status: typeof item.status === "string" ? item.status : "",
      last_updated: typeof item.last_updated === "string" ? item.last_updated : "",
    };
  });

  return {
    tool_use_id: candidate.tool_use_id as string | undefined,
    reason,
    playbooks,
  };
}
